import React from 'react'
import { alpha } from '@mui/material'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandOutlineButton, brandPrimaryButton } from '../_helpers/brandButtons'
import { brandHeading, brandText } from '@/utils/brand-typography'
import { ModalSmall } from '@/modules/base/components/modal'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { VARIANTS as BUTTON_VARIANTS } from '@/modules/base/components/buttons/square/button-square'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  paper: {
    width: 460,
    maxWidth: 'calc(100% - 32px)',
    margin: 16,
    borderRadius: theme.borderRadiusExtraExtraLarge,
    border: `1px solid ${theme.palette.brand.divider}`,
    fontFamily: theme.brandFontFamilies.body,
    // ModalSmall only passes the title class on to the text, its wrapper keeps a wider padding
    '& .MuiDialogTitle-root': {
      padding: '32px 32px 8px',
    },
  },
  backdrop: {
    background: alpha(theme.palette.primary[800], 0.4),
    '@supports (backdrop-filter: blur(4px))': {
      backdropFilter: 'blur(4px)',
    },
  },
  title: {
    margin: 0,
    color: theme.palette.brand.text.heading,
    ...brandHeading(theme, 'h5'),
  },
  content: {
    padding: '8px 32px 0',
  },
  description: {
    margin: 0,
    color: theme.palette.brand.text.secondary,
    ...brandText(theme, 'regular'),
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 16,
    padding: '24px 32px 32px',
    '& > :not(:first-of-type)': {
      marginLeft: 0,
    },
    [theme.breakpoints.down(768)]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  button: {
    borderRadius: theme.borderRadiusPill,
    ...brandText(theme, 'small', 'medium'),
    fontSize: theme.brandTypography.text.regular.fontSize,
  },
  cancelButton: {
    ...brandOutlineButton(theme),
    fontWeight: theme.brandTypography.weights.normal,
  },
  continueButton: {
    ...brandPrimaryButton(theme),
    fontWeight: theme.brandTypography.weights.normal,
  },
}))

interface CancelCheckoutDialogProps {
  open: boolean
  onCancelPurchase: () => void
  onContinue: () => void
}

/**
 * "Kauf abbrechen?" - continuing is the main action, closing the dialog in any other way
 * (backdrop, escape) continues as well, so nothing is lost by accident.
 */
const CancelCheckoutDialog = ({ open, onCancelPurchase, onContinue }: CancelCheckoutDialogProps) => {
  const classes = useStyles()
  const texts = lang.upgrade.wizard.cancelDialog

  return (
    <ModalSmall
      open={open}
      title={texts.title}
      onClose={onContinue}
      dataQsId='checkout-cancel-dialog'
      classes={{ paper: classes.paper, title: classes.title, content: classes.content, actions: classes.actions }}
      slotProps={{ backdrop: { className: classes.backdrop } }}
      actions={[
        <ButtonSquare
          key='cancel'
          variant={BUTTON_VARIANTS.outline}
          className={`${classes.button} ${classes.cancelButton}`}
          label={texts.confirm}
          dataQsId='checkout-cancel-dialog-cancel'
          onClick={onCancelPurchase}
        />,
        <ButtonSquare
          key='continue'
          className={`${classes.button} ${classes.continueButton}`}
          label={texts.abort}
          dataQsId='checkout-cancel-dialog-continue'
          onClick={onContinue}
        />,
      ]}>
      <p className={classes.description}>{texts.description}</p>
    </ModalSmall>
  )
}

export default CancelCheckoutDialog
